/**
 * workspaceContext.ts — per-view "where the user is" snapshots for the chat.
 *
 * The chat agent answers better when it knows what the user is looking at:
 * the active view, open/active files, the node/edge modal that's open, the
 * table being browsed. App composes the state it already mirrors (view,
 * graph selection, code tabs, dashboards, workflow tabs) and panels
 * self-register a provider for state only they hold (DataPanel's active
 * table, PlotPanel's chart editor, WorkflowPanel's step selection) — the same
 * registration pattern as the AskBox's InsertTarget registry
 * (src/ask/registry.ts), so nothing threads through App's props.
 *
 * The snapshot rides the POST /chat body as `context` and lands in the system
 * prompt's "Where the user is" section (backend/chat/agent.py). Read at SEND
 * time, never cached — it must reflect the moment the user hit enter.
 */

export type ViewContextSnapshot = Record<string, unknown>;
export type ViewContextProvider = () => ViewContextSnapshot | null;

const providers = new Map<string, ViewContextProvider>();

/** Register the snapshot provider for a view (e.g. 'data'). Returns an
 *  unregister function — panels call it on unmount. */
export function registerViewContext(view: string, provider: ViewContextProvider): () => void {
  providers.set(view, provider);
  return () => {
    if (providers.get(view) === provider) {
      providers.delete(view);
    }
  };
}

/** The snapshot registered for a view, or null when the view's panel isn't
 *  mounted or reported nothing. Never throws — context is best-effort. */
export function viewContextSnapshot(view: string): ViewContextSnapshot | null {
  const provider = providers.get(view);
  if (!provider) return null;
  try {
    return provider();
  } catch {
    return null;
  }
}

/** Test hook: drop all registrations. */
export function _clearViewContextForTest(): void {
  providers.clear();
}
